import { useEffect, useRef, useState } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { fractionAt } from '../../lib/voicePeaks';

interface WaveformProps {
  /** Bar heights, 0-100. */
  bars: number[];
  /** 0-1: bars before this point use `playedColor`. */
  progress?: number;
  color: string;
  playedColor?: string;
  height?: number;
  /** Called with 0-1 when the waveform is clicked or tapped. */
  onSeek?: (fraction: number) => void;
  /**
   * While dragging along it (D221): where it would land, 0-1, then null
   * when the drag is cancelled. Letting go seeks with `onSeek`.
   */
  onScrub?: (fraction: number | null) => void;
  label?: string;
}

// Shown at least this tall so silence still reads as a waveform.
const MIN_BAR_PERCENT = 8;
// The narrowest a bar and its gap may be (2px + 2px).
const BAR_PITCH_PX = 4;

/**
 * At most `count` bars, merging neighbours and keeping each group's peak, so
 * a narrow waveform keeps its shape instead of spilling out of its row.
 */
export function fitBars(bars: number[], count: number): number[] {
  if (count <= 0) return [];
  if (bars.length <= count) return bars;
  return Array.from({ length: count }, (_, i) => {
    const start = Math.floor((i * bars.length) / count);
    const end = Math.max(
      start + 1,
      Math.floor(((i + 1) * bars.length) / count)
    );
    return Math.max(...bars.slice(start, end));
  });
}

/** How many bars fit in `widthPx` at the narrowest bar and gap. */
export function barsThatFit(widthPx: number): number {
  return Math.max(1, Math.floor((widthPx + 2) / BAR_PITCH_PX));
}

export function Waveform({
  bars,
  progress = 0,
  color,
  playedColor = color,
  height = 28,
  onSeek,
  onScrub,
  label,
}: WaveformProps) {
  const dragging = useRef(false);
  // Fit the bars to the space the row leaves (a 240px voice pill on a phone
  // has room for about 32 of the 48 recorded bars).
  const ref = useRef<HTMLDivElement>(null);
  const [fit, setFit] = useState<number | null>(null);
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const measure = () => {
      const width = element.clientWidth;
      if (width > 0) setFit(barsThatFit(width));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  const shown = fit === null ? bars : fitBars(bars, fit);

  return (
    <Flex
      ref={ref}
      align="center"
      gap="2px"
      h={`${height}px`}
      flex="1"
      minW={0}
      cursor={onSeek ? 'pointer' : undefined}
      role={onSeek ? 'slider' : undefined}
      aria-label={label}
      aria-valuemin={onSeek ? 0 : undefined}
      aria-valuemax={onSeek ? 100 : undefined}
      aria-valuenow={onSeek ? Math.round(progress * 100) : undefined}
      tabIndex={onSeek ? 0 : undefined}
      // Drag to scrub (D221); a tap is a drag that didn't move.
      sx={onScrub ? { touchAction: 'none' } : undefined}
      onPointerDown={
        onSeek && onScrub
          ? (event) => {
              dragging.current = true;
              event.currentTarget.setPointerCapture?.(event.pointerId);
              onScrub(
                fractionAt(
                  event.clientX,
                  event.currentTarget.getBoundingClientRect()
                )
              );
            }
          : undefined
      }
      onPointerMove={
        onScrub
          ? (event) => {
              if (!dragging.current) return;
              onScrub(
                fractionAt(
                  event.clientX,
                  event.currentTarget.getBoundingClientRect()
                )
              );
            }
          : undefined
      }
      onPointerUp={
        onSeek && onScrub
          ? (event) => {
              if (!dragging.current) return;
              dragging.current = false;
              onSeek(
                fractionAt(
                  event.clientX,
                  event.currentTarget.getBoundingClientRect()
                )
              );
            }
          : undefined
      }
      onPointerCancel={
        onScrub
          ? () => {
              dragging.current = false;
              onScrub(null);
            }
          : undefined
      }
      onClick={
        onSeek && !onScrub
          ? (event) =>
              onSeek(
                fractionAt(
                  event.clientX,
                  event.currentTarget.getBoundingClientRect()
                )
              )
          : undefined
      }
      onKeyDown={
        onSeek
          ? (event) => {
              const step =
                event.key === 'ArrowRight'
                  ? 0.05
                  : event.key === 'ArrowLeft'
                    ? -0.05
                    : 0;
              if (!step) return;
              event.preventDefault();
              onSeek(Math.min(1, Math.max(0, progress + step)));
            }
          : undefined
      }
    >
      {shown.map((value, index) => (
        <Box
          key={index}
          flex="1"
          minW="2px"
          maxW="4px"
          borderRadius="full"
          h={`${Math.max(MIN_BAR_PERCENT, value)}%`}
          bg={(index + 0.5) / shown.length <= progress ? playedColor : color}
          transition="height 80ms linear"
        />
      ))}
    </Flex>
  );
}
